// Theme preference: the three states the header control cycles through.
//
// "system" is the default and is stored as the ABSENCE of the data-theme
// attribute, which is what lets the `prefers-color-scheme` block in tokens.css
// apply. The two explicit choices stamp data-theme and win over the OS in both
// directions — a student on a dark phone can still force light for a bright
// classroom, which a two-state toggle cannot express.

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'cwy-theme';

/** The order the header control cycles in. */
export const THEME_ORDER: ThemePreference[] = ['system', 'light', 'dark'];

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'system';
  } catch {
    // Safari in private mode throws on localStorage access rather than
    // returning null, and a theme preference is never worth breaking boot over.
    return 'system';
  }
}

export function systemTheme(): ResolvedTheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

/**
 * Apply a preference to the document. Sets data-theme for the explicit choices
 * and removes it for "system", then syncs the address-bar colour so the browser
 * chrome on Android matches the canvas the student is actually looking at.
 */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);

  const resolved = resolveTheme(preference);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolved === 'dark' ? '#14160F' : '#2E4A1C');
  return resolved;
}

export function storePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Non-fatal: the theme still applies for this session, it just will not
    // survive a reload.
  }
}

export function nextPreference(current: ThemePreference): ThemePreference {
  return THEME_ORDER[(THEME_ORDER.indexOf(current) + 1) % THEME_ORDER.length];
}

export const THEME_LABEL: Record<ThemePreference, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};
